﻿import { Tooltip } from '@sgme/ui';
import type { ReactElement } from 'react';
import { useIntl } from 'react-intl';

import { useFlexiForwardSelector } from '@/App/tiles/FlexiForward/FlexiForwardContext.ts';
import { FlexiForwardFormField } from '@/App/tiles/FlexiForward/FlexiForwardTile/common/form/FlexiForwardFormField';
import { FormSelect } from '@/App/tiles/FlexiForward/FlexiForwardTile/common/form/FormSelect';
import { HedgeFormGroup } from '@/App/tiles/FlexiForward/FlexiForwardTile/FlexiForwardHedgesForm/HedgeFormGroup.tsx';
import { PremiumDatePicker } from '@/App/tiles/FlexiForward/FlexiForwardTile/FlexiForwardHedgesForm/PremiumDatePicker.tsx';
import { useUserContext } from '@/context/UserContext.tsx';
import { useFlexiForwardPropertyChangedHandler } from '@/store/flexi-forward/hooks/useFlexiForwardPropertyChangedHandler.ts';
import { DEFAULT_HEDGE_TYPE, type HedgeType, POSSIBLE_HEDGE_TYPE } from '@/store/flexi-forward/state';

export function FlexiForwardHedgesForm({ tileId }: { tileId: string }): ReactElement {
  const { formatMessage } = useIntl();

  const { currentUser } = useUserContext();

  const { hedgeType: selectedHedgeType } = useFlexiForwardSelector((flexiForward) => {
    const properties = flexiForward.properties;

    return {
      hedgeType: properties.hedgeType,
    };
  });

  function formatHedgeTypeOptions(value: HedgeType): string {
    return formatMessage({ id: `flexiForwardTile.mainFields.hedgeType.option.${value}` });
  }

  const isInternalSales = currentUser.userType === 'Internal Sales';
  const hedgeTypeExtra =
    isInternalSales && selectedHedgeType === 'Live' ? (
      <Tooltip
        position="right"
        contents={formatMessage({ id: 'flexiForwardTile.mainFields.hedgeType.tooltip.message' })}
      >
        <i className="icon icon-sm cursor-default line-height-1 text-warning" data-e2e="HedgeTypeInfo">
          warning
        </i>
      </Tooltip>
    ) : undefined;

  const handleHedgeTypeChanged = useFlexiForwardPropertyChangedHandler<HedgeType>(
    tileId,
    'Delta Hedge type changed',
    (hedgeType) => ({ hedgeType }),
  );

  return (
    <div className="p-8px">
      {/* PREMIUM DATE */}
      <PremiumDatePicker tileId={tileId} />

      {/* HEDGE TYPE */}
      <FlexiForwardFormField formattedMessageId="flexiForwardTile.mainFields.hedgeType.label" extra={hedgeTypeExtra}>
        <FormSelect<HedgeType>
          values={POSSIBLE_HEDGE_TYPE}
          selectedValue={selectedHedgeType ?? DEFAULT_HEDGE_TYPE}
          onChange={handleHedgeTypeChanged}
          formatValue={formatHedgeTypeOptions}
          data-e2e="hedge-type-select"
        />
      </FlexiForwardFormField>

      {/* HEDGE EXCHANGE SPOT FORM FIELDS */}
      <HedgeFormGroup tileId={tileId} />
    </div>
  );
}
